"use client";

import React from "react";
import { motion } from "framer-motion";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface TechSpec {
    index: string;
    label: string;
    tech: string;
    detail: string;
}

export interface GuestiqoTechStackBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    specs?: TechSpec[];
}

export default function GuestiqoTechStackBlock({
    tag = "// Architectural Stack",
    title = "Technology Specifications",
    description = "All services are chosen for maximum availability, edge performance speed, and decoupled multi-tenant architecture.",
    specs = [
        { index: "01", label: "Core Frontend Framework", tech: "Next.js 15 (App Router)", detail: "Edge-routed page streaming, server components, and pre-compiled client segments for sub-200ms initial response." },
        { index: "02", label: "Platform Codebase", tech: "Strict TypeScript", detail: "End-to-end typing from model database schemas down to UI component states, ensuring robust compilation." },
        { index: "03", label: "Style System & Tokens", tech: "Tailwind CSS + Headless UI", detail: "Strict spacing grids and color token architectures tailored for mobile thumb reaches and accessibility standards." },
        { index: "04", label: "Cloud Engine & Database", tech: "Firebase Serverless Stack", detail: "Firestore real-time syncing with offline client data caching, and global media storage CDN layers." },
        { index: "05", label: "Backend Core Middleware", tech: "Node.js 20 Cloud Functions v2", detail: "Decoupled cloud functions for subdomain provisioning, billing syncs, email dispatches, and QR generation." },
        { index: "06", label: "Edge & Custom DNS Routing", tech: "Firebase Hosting + Cloudflare API", detail: "Dynamic SSL provisioning for custom subdomains (e.g. seasidevilla.guestiqo.uk) with low-latency CDN routing." },
        { index: "07", label: "Data Isolation Pipeline", tech: "Subdomain-Tenant Context", detail: "Isolated data queries checked at security layers by tenant IDs, ensuring strict multi-tenant boundary parameters." },
        { index: "08", label: "Operational Telemetry", tech: "Custom Micro-Event Pipeline", detail: "Anonymized page views, guidebook opens, and QR scan hits gathered locally without cookies." }
    ]
}: GuestiqoTechStackBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF7F2] border-t border-b border-[#1C1C1C]/5 w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-20">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {description && (
                        <p className="text-[#5E5E5E] text-sm mt-3 font-sans font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* Spec-Table List */}
                <div className="border-t border-[#1C1C1C]/10">
                    {specs.map((spec, i) => (
                        <motion.div 
                            key={i}
                            {...fadeUp}
                            transition={{ delay: i * 0.05 }}
                            className="group border-b border-[#1C1C1C]/10 py-6 md:py-8 flex flex-col md:flex-row md:items-start justify-between gap-4 md:gap-0 hover:bg-[#F7F3EB]/40 px-4 -mx-4 transition-colors duration-300"
                        >
                            <div className="flex items-center gap-6 md:w-1/3">
                                <span className="text-xs font-mono text-[#8C8276]">{spec.index}</span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8C8276] font-semibold">{spec.label}</span>
                            </div>
                            <div className="md:w-1/3">
                                <h4 className={`${cormorant.className} text-xl md:text-2xl text-[#1C1C1C] font-medium`}>
                                    {spec.tech}
                                </h4>
                            </div>
                            <div className="md:w-1/3 max-w-sm">
                                <p className="text-xs text-[#5E5E5E] leading-relaxed font-sans font-light">
                                    {spec.detail}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
